import { Controller } from "@hotwired/stimulus";

const TABLE_CONTENT_TYPE = "application/vnd.fragallia.table+html";
const BLOCK_TAGS = "div, p, h1, h2, h3, h4, h5, h6, ul, ol, li, blockquote, pre, figure";

// Edits one cell of a Table (a Trix content attachment) at a time, in a dialog holding an
// inline-only Trix editor. Saving writes the cell back into the attachment's content, so the
// change is stored by the form's normal save; the Table's rows and columns never change.
export default class extends Controller {
  static targets = ["editor", "dialog", "cellEditor"];

  open(event) {
    const cell = event.target.closest("th, td");
    const figure = cell?.closest("figure[data-trix-id]");
    const attachment = figure && this.attachment(figure);
    if (!attachment) return;

    this.current = { attachment, index: [...figure.querySelectorAll("th, td")].indexOf(cell) };
    const editor = this.cellEditorTarget.editor;
    editor.loadHTML(this.cells(attachment)[this.current.index].innerHTML);
    this.dialogTarget.showModal();
    this.cellEditorTarget.focus();
    editor.setSelectedRange(editor.getDocument().getLength() - 1);
  }

  save() {
    if (!this.current) return;

    const { attachment, index } = this.current;
    const table = this.table(attachment);
    table.querySelectorAll("th, td")[index].innerHTML = this.inlineHTML(this.cellEditorTarget.value);
    attachment.setAttributes({ content: table.outerHTML });
    this.dialogTarget.close();
  }

  cancel() {
    this.dialogTarget.close();
  }

  closed() {
    this.current = null;
    this.editorTarget.focus();
  }

  // Enter saves the cell: a cell is one line of text.
  keydown(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return;

    event.preventDefault();
    this.save();
  }

  refuseFile(event) {
    event.preventDefault();
  }

  // The Table attachment behind a figure in the editor, or nothing for any other attachment.
  attachment(figure) {
    const attachment = this.editorTarget.editor.getDocument().getAttachmentById(Number(figure.dataset.trixId));
    return attachment?.getContentType() === TABLE_CONTENT_TYPE ? attachment : null;
  }

  table(attachment) {
    const template = document.createElement("template");
    template.innerHTML = attachment.getAttribute("content");
    return template.content.querySelector("table");
  }

  cells(attachment) {
    return this.table(attachment).querySelectorAll("th, td");
  }

  // The cell editor's HTML as inline content only: blocks (paragraphs, and lists or headings
  // pasted in) are unwrapped and line breaks become spaces. Trix keeps edge spaces as &nbsp;; a
  // cell wants plain ones.
  inlineHTML(html) {
    const template = document.createElement("template");
    template.innerHTML = html.replaceAll("&nbsp;", " ");
    template.content.querySelectorAll("br").forEach((br) => br.replaceWith(" "));
    template.content.querySelectorAll(BLOCK_TAGS).forEach((block) => block.replaceWith(...block.childNodes, " "));
    return template.innerHTML.replace(/\s+/g, " ").trim();
  }
}
